import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bomApi, type BomLineItem } from "@/lib/bom";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BomItemPicker } from "./BomItemPicker";
import { type BomItemSummary } from "@/lib/bom-items";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BomEditor } from "./BomEditor";
import { InfoHint } from "@/components/ui/info-hint";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface QtyCol { quantity: number; overs: number; markupPct: number; adjustAmt: number; }

function parseCols(json: string | null): QtyCol[] {
  if (!json) return [];
  try { return JSON.parse(json) as QtyCol[]; } catch { return []; }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BuildPricePanelProps {
  bomId: number;
  quoteId: number;
  disabled: boolean;
}

// ---------------------------------------------------------------------------
// BuildPricePanel
//
// Renders a lightweight read-only (for now) list of BOM components with their
// live per-component line value from the first pricing column, plus a footer
// showing the column's total line price ex-GST.
//
// Data fetch: uses bomApi.get + bomApi.price directly via useQuery (same
// underlying functions as useBom, but this component is standalone — it does
// not share a QueryClient with BomEditor and doesn't need a debounced
// side-effect; useQuery's cache deduplication handles concurrent renders).
// ---------------------------------------------------------------------------

export function BuildPricePanel({ bomId, quoteId, disabled }: BuildPricePanelProps) {
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [qtyText, setQtyText] = useState<string>("0");
  const [advanced, setAdvanced] = useState(false);

  // BOM data
  const bomQ = useQuery({
    queryKey: ["bom", bomId],
    queryFn: () => bomApi.get(bomId),
  });
  const bom = bomQ.data ?? null;

  // Parse quantity columns; column[0] = the quoted quantity
  const cols = parseCols(bom?.quantityColsJson ?? null);

  // Seed the local qty text from the BOM whenever it (re)loads
  useEffect(() => {
    const first = cols[0];
    if (first) {
      setQtyText(String(first.quantity));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bom?.rowVersion]);

  // Live pricing — enabled only once BOM is loaded and has columns
  const priceQ = useQuery({
    queryKey: ["bom-price", bomId, bom?.quantityColsJson, bom?.items?.length],
    enabled: bom != null && cols.length > 0,
    queryFn: () =>
      bomApi.price(bomId, {
        quantities: cols.map((c) => c.quantity),
        overs: cols.map((c) => c.overs),
        markupPcts: cols.map((c) => c.markupPct),
        adjustAmts: cols.map((c) => c.adjustAmt),
      }),
  });

  // Build id → lineValueAud lookup from column[0].lineBreakdown
  const col0 = priceQ.data?.columns?.[0] ?? null;
  const lineValue = new Map<number, number>(
    (col0?.lineBreakdown ?? [])
      .filter((b) => b.bomLineItemId != null)
      .map((b) => [b.bomLineItemId as number, b.lineValueAud]),
  );

  // Itemised components are deliberately excluded from the rolled-up line price
  // (engine parity). Surface them so the per-component values still reconcile
  // with the smaller line-price total.
  const itemisedItems = (bom?.items ?? []).filter((it: BomLineItem) => it.itemiseOnQuote);
  const itemisedTotalAud = itemisedItems.reduce(
    (sum: number, it: BomLineItem) => sum + (lineValue.get(it.bomLineItemId) ?? 0),
    0,
  );

  // ---------------------------------------------------------------------------
  // Shared invalidation helper
  // ---------------------------------------------------------------------------

  async function invalidateAll() {
    await qc.invalidateQueries({ queryKey: ["bom", bomId] });
    await qc.invalidateQueries({ queryKey: ["bom-price", bomId] });
    await qc.invalidateQueries({ queryKey: ["quote-items", quoteId] });
    await qc.invalidateQueries({ queryKey: ["quote", quoteId] });
  }

  // ---------------------------------------------------------------------------
  // Quoted quantity commit handler
  // ---------------------------------------------------------------------------

  async function commitQty() {
    const n = parseInt(qtyText, 10);
    const first = cols[0];
    if (!Number.isFinite(n) || !first || n === first.quantity) return;
    const next = cols.map((c, i) => (i === 0 ? { ...c, quantity: n } : c));
    await bomApi.patch(bomId, { quantityColsJson: JSON.stringify(next) }, bom!.rowVersion);
    await invalidateAll();
  }

  // ---------------------------------------------------------------------------
  // Add component handler
  // ---------------------------------------------------------------------------

  async function addComponent(m: BomItemSummary) {
    await bomApi.addItem(bomId, {
      bomItemId: m.bomItemId,
      itemName: m.name,
      calcType: m.calcType,
      calcFactor: m.calcFactor,
      unitCost: m.unitCost,
      unitCurrencyCode: m.unitCurrencyCode,
      priceByRange: m.priceByRange,
      unitCostByRangeJson: m.unitCostByRangeJson,
      supplierId: m.supplierId,
      itemiseOnQuote: m.itemiseOnQuote,
    });
    setPicking(false);
    await invalidateAll();
  }

  // ---------------------------------------------------------------------------
  // Remove component handler
  // ---------------------------------------------------------------------------

  async function removeComponent(it: BomLineItem) {
    await bomApi.deleteItem(bomId, it.bomLineItemId, it.rowVersion);
    await invalidateAll();
  }

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (bomQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (bomQ.isError || !bom) {
    return <p className="text-sm text-muted-foreground">Couldn't load this line.</p>;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="pl-6 py-2 w-full space-y-2">
      {/* Header row: Qty and Actions */}
      <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap" htmlFor={`bpp-qty-${bomId}`}>
            Quote Qty
          </label>
          <InfoHint label="The quantity this line is quoted at. Changing it re-prices every component." />
          <Input
            id={`bpp-qty-${bomId}`}
            aria-label="Quantity"
            type="number"
            className="h-6 w-16 text-xs text-center ml-1"
            value={qtyText}
            disabled={disabled}
            onChange={(e) => setQtyText(e.target.value)}
            onBlur={() => { void commitQty(); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur(); }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {picking ? (
            <BomItemPicker onPick={addComponent} onCancel={() => setPicking(false)} />
          ) : (
            <Button variant="outline" size="sm" type="button" className="h-6 px-3 text-[10px] rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold uppercase tracking-wider" disabled={disabled} onClick={() => setPicking(true)}>
              + Component
            </Button>
          )}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" type="button" className="h-6 px-3 text-[10px] rounded-full border-transparent bg-muted text-muted-foreground hover:bg-muted/80 font-semibold uppercase tracking-wider" disabled={disabled} onClick={() => setAdvanced(true)}>
              Advanced
            </Button>
            <InfoHint label="Full editor: quantity price-breaks, decoration, currency and price tables." />
          </div>
        </div>
      </div>

      {/* Components list */}
      <ul className="space-y-0.5">
        {bom.items.length === 0 ? (
          <li className="py-1 text-xs text-muted-foreground italic">No components added.</li>
        ) : (
          bom.items.map((it: BomLineItem) => (
            <li key={it.bomLineItemId} className="flex items-center justify-between text-xs py-1">
              <span className="flex items-center gap-2">
                <span className="text-foreground">{it.itemName}</span>
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider bg-muted px-1.5 py-0.5 rounded-sm">
                  {it.calcType}
                </span>
                {it.itemiseOnQuote && (
                  <span className="text-[9px] text-warning-foreground bg-warning/15 px-1.5 py-0.5 rounded-sm uppercase tracking-wider" title="Priced separately — excluded from this line's price (see the subtotal below)">
                    Itemised
                  </span>
                )}
              </span>
              <span className="flex items-center gap-3">
                <span className="tabular-nums text-foreground">
                  {lineValue.has(it.bomLineItemId)
                    ? formatMoney(Math.round(lineValue.get(it.bomLineItemId)! * 100))
                    : "—"}
                </span>
                {!disabled && (
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive" aria-label={`Remove ${it.itemName}`} onClick={() => { void removeComponent(it); }}>
                    ×
                  </Button>
                )}
              </span>
            </li>
          ))
        )}
      </ul>

      {/* Totals */}
      <div className="flex flex-col items-end pt-1 gap-1 border-t border-border/50">
        {itemisedItems.length > 0 && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <span>Itemised separately</span>
            <span className="tabular-nums">{formatMoney(Math.round(itemisedTotalAud * 100))}</span>
            <span>— excluded from line price</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-success">
          <InfoHint label="Total for this line before GST. Itemised components are excluded (shown above)." />
          <span className="font-normal mr-1">Line Price</span>
          {col0 ? formatMoney(Math.round(col0.totalPriceAud * 100)) : "—"} 
          <span className="text-[9px] font-normal uppercase text-success/80">ex-GST</span>
        </div>
      </div>

      <Dialog
        open={advanced}
        onOpenChange={(o) => {
          setAdvanced(o);
          if (!o) { void invalidateAll(); }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Advanced build settings</DialogTitle>
            <DialogDescription className="sr-only">
              Edit quantity tiers, decoration and sheet settings for this quote line.
            </DialogDescription>
          </DialogHeader>
          <BomEditor bomId={bomId} parentType="Quote" parentId={quoteId} productId={null} />
          <DialogFooter>
            <Button onClick={() => { setAdvanced(false); void invalidateAll(); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
